import { createSlice } from "@reduxjs/toolkit";
import {
  createPlan,
  getAllPlans,
  getPlanById,
  updatePlanById,
  deletePlanById,
  getAllPrivatePlans,
  getAllPublicPlans,
  getCustomerUserAssignAndPublicPlan,
} from "./planAction";

interface Pagination {
  totalPlans: number;
  totalPages: number;
  currentPage: number;
  limit: number;
}

interface PlanState {
  plans: {
    id: string;
    planName: string;
    price: number;
    priceId?: string;
    description: string;
    duration: "monthly" | "yearly" | "custom";
    features: string[];
    fleetEstimatorReportLimit: number;
    siteEstimatorReportLimit: number;
    isPopular?: boolean;
    isEmbedded?: boolean;
    isWhiteList?: boolean;
    isFleetEstimator?: boolean;
    isSiteEstimator?: boolean;
    mode: "public" | "private";
    userId: string | null;
    assignName?: string | null;
    createdAt: string;
    updatedAt: string;
  }[];
  publicPlans: {
    id: string;
    planName: string;
    price: number;
    priceId?: string;
    description: string;
    duration: "monthly" | "yearly" | "custom";
    features: string[];
    fleetEstimatorReportLimit: number;
    siteEstimatorReportLimit: number;
    isPopular?: boolean;
    isEmbedded?: boolean;
    isWhiteList?: boolean;
    isFleetEstimator?: boolean;
    isSiteEstimator?: boolean;
    mode: "public" | "private";
    userId: string | null;
    assignName?: string | null;
    createdAt: string;
    updatedAt: string;
  }[];
  privatePlans: {
    id: string;
    planName: string;
    price: number;
    priceId?: string;
    description: string;
    duration: "monthly" | "yearly" | "custom";
    features: string[];
    fleetEstimatorReportLimit: number;
    siteEstimatorReportLimit: number;
    isPopular?: boolean;
    isEmbedded?: boolean;
    isWhiteList?: boolean;
    isFleetEstimator?: boolean;
    isSiteEstimator?: boolean;
    mode: "public" | "private";
    userId: string | null;
    assignName?: string | null;
    createdAt: string;
    updatedAt: string;
  }[];
  customerPlans: {
    id: string;
    planName: string;
    price: number;
    priceId?: string;
    description: string;
    duration: "monthly" | "yearly" | "custom";
    features: string[];
    fleetEstimatorReportLimit: number;
    siteEstimatorReportLimit: number;
    isPopular?: boolean;
    isEmbedded?: boolean;
    isWhiteList?: boolean;
    isFleetEstimator?: boolean;
    isSiteEstimator?: boolean;
    mode: "public" | "private";
    userId: string | null;
    assignName?: string | null;
    createdAt: string;
    updatedAt: string;
  }[];
  selectedPlan: {
    id: string;
    planName: string;
    price: number;
    priceId?: string;
    description: string;
    duration: "monthly" | "yearly" | "custom";
    features: string[];
    fleetEstimatorReportLimit: number;
    siteEstimatorReportLimit: number;
    isPopular?: boolean;
    isEmbedded?: boolean;
    isWhiteList?: boolean;
    isFleetEstimator?: boolean;
    isSiteEstimator?: boolean;
    mode: "public" | "private";
    userId: string | null;
    assignName?: string | null;
    createdAt: string;
    updatedAt: string;
  } | null;
  pagination: Pagination | null;
  publicPagination: Pagination | null;
  privatePagination: Pagination | null;
  customerPagination: Pagination | null;
  loading: boolean;
  error: string | null;
}

const initialState: PlanState = {
  plans: [],
  publicPlans: [],
  privatePlans: [],
  customerPlans: [],
  selectedPlan: null,
  pagination: null,
  publicPagination: null,
  privatePagination: null,
  customerPagination: null,
  loading: false,
  error: null,
};

const planSlice = createSlice({
  name: "planManagement",
  initialState,
  reducers: {
    clearPlanMessages(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // CREATE PLAN
      .addCase(createPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPlan.fulfilled, (state, action) => {
        state.loading = false;
        const plan = action.payload.data?.plan;
        if (plan) {
          state.plans.push(plan);
          if (plan.mode === "public") {
            state.publicPlans.push(plan);
          } else if (plan.mode === "private") {
            state.privatePlans.push(plan);
          }
        }
        state.error = null;
      })
      .addCase(createPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Plan creation failed";
      })
      // GET ALL PLANS
      .addCase(getAllPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload.data?.plans || [];
        state.pagination = action.payload.data?.pagination || null;
        state.error = null;
      })
      .addCase(getAllPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to retrieve plans";
      })
      // GET ALL PUBLIC PLANS
      .addCase(getAllPublicPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllPublicPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.publicPlans = action.payload.data?.plans || [];
        state.publicPagination = action.payload.data?.pagination || null;
        state.error = null;
      })
      .addCase(getAllPublicPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to retrieve public plans";
      })
      // GET ALL PRIVATE PLANS
      .addCase(getAllPrivatePlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllPrivatePlans.fulfilled, (state, action) => {
        state.loading = false;
        state.privatePlans = action.payload.data?.plans || [];
        state.privatePagination = action.payload.data?.pagination || null;
        state.error = null;
      })
      .addCase(getAllPrivatePlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to retrieve private plans";
      })
      // GET CUSTOMER USER ASSIGN AND PUBLIC PLAN
      .addCase(getCustomerUserAssignAndPublicPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getCustomerUserAssignAndPublicPlan.fulfilled,
        (state, action) => {
          state.loading = false;
          state.customerPlans = action.payload.data?.plans || [];
          state.customerPagination = action.payload.data?.pagination || null;
          state.error = null;
        }
      )
      .addCase(getCustomerUserAssignAndPublicPlan.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ||
          "Failed to retrieve customer assigned and public plans";
      })
      // GET PLAN BY ID
      .addCase(getPlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPlanById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPlan = action.payload.data?.plan || null;
        state.error = null;
      })
      .addCase(getPlanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to retrieve plan";
      })
      // UPDATE PLAN
      .addCase(updatePlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePlanById.fulfilled, (state, action) => {
        state.loading = false;
        const updatedPlan = action.payload.data?.plan;
        if (updatedPlan) {
          state.plans = state.plans.map((plan) =>
            plan.id === updatedPlan.id ? updatedPlan : plan
          );
          state.publicPlans = state.publicPlans.map((plan) =>
            plan.id === updatedPlan.id ? updatedPlan : plan
          );
          state.privatePlans = state.privatePlans.map((plan) =>
            plan.id === updatedPlan.id ? updatedPlan : plan
          );
          state.customerPlans = state.customerPlans.map((plan) =>
            plan.id === updatedPlan.id ? updatedPlan : plan
          );
          if (state.selectedPlan?.id === updatedPlan.id) {
            state.selectedPlan = updatedPlan;
          }
        }
        state.error = null;
      })
      .addCase(updatePlanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Plan update failed";
      })
      // DELETE PLAN
      .addCase(deletePlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePlanById.fulfilled, (state, action) => {
        state.loading = false;
        const deletedPlanId = action.meta.arg;
        state.plans = state.plans.filter((plan) => plan.id !== deletedPlanId);
        state.publicPlans = state.publicPlans.filter(
          (plan) => plan.id !== deletedPlanId
        );
        state.privatePlans = state.privatePlans.filter(
          (plan) => plan.id !== deletedPlanId
        );
        state.customerPlans = state.customerPlans.filter(
          (plan) => plan.id !== deletedPlanId
        );
        if (state.selectedPlan?.id === deletedPlanId) {
          state.selectedPlan = null;
        }
        state.error = null;
      })
      .addCase(deletePlanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Plan deletion failed";
      });
  },
});

export const { clearPlanMessages } = planSlice.actions;
export default planSlice.reducer;
